import { useEffect, useState, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import {
  Article, ArrowRight, Link as LinkIcon, FilePdf, Printer, PencilSimple, TrashSimple,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { StorageThumb, StorageLink } from '@/components/StorageMedia';
import { shizaiApi } from '../shizaiApi';
import { STATUS_META, PRIORITY_META } from './shared';
import TraceabilityPanel from '@/pages/trace/TraceabilityPanel';

/* ─── My requests tracking tab ─── */
export function MyRequests({ categories, onEdit }) {
  const [requests, setRequests] = useState([]);
  const [fabs, setFabs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [ddFilter, setDdFilter] = useState('all');

  const catMap = useMemo(() => Object.fromEntries((categories || []).map((c) => [c.id, c])), [categories]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await shizaiApi.myRequests();
      setRequests(rows);
      shizaiApi.myFabrications().then((f) => setFabs(f || [])).catch(() => setFabs([]));
    } catch (_e) {
      toast.error('依頼の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const cancel = async (r) => {
    if (!window.confirm(`依頼 ${r.request_no} を取消しますか？`)) return;
    try {
      await shizaiApi.cancelMyRequestSoft(r.id);
      toast.success('依頼を取消しました');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '取消に失敗しました');
    }
  };

  const ddOptions = useMemo(() => {
    const m = new Map();
    requests.forEach((r) => { if (r.delivery_destination?.id) m.set(r.delivery_destination.id, r.delivery_destination.name); });
    return [...m.entries()];
  }, [requests]);

  const rows = requests
    .filter((r) => statusFilter === 'all' || r.status === statusFilter)
    .filter((r) => ddFilter === 'all' || r.delivery_destination?.id === ddFilter);

  return (
    <div className="space-y-4">
      {/* Filter pills */}
      <div className="flex items-center gap-2 flex-wrap">
        {['all', 'open', 'ordered', 'partial', 'received', 'cancelled'].map((k) => {
          const count = k === 'all' ? requests.length : requests.filter((r) => r.status === k).length;
          const active = statusFilter === k;
          return (
            <button
              key={k}
              onClick={() => setStatusFilter(k)}
              data-testid={`pr-mine-filter-${k}`}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors flex items-center gap-1.5 ${
                active ? 'bg-zinc-900 text-white' : 'bg-white border border-zinc-200 text-zinc-600 hover:bg-zinc-50'
              }`}
            >
              {k === 'all' ? 'すべて' : STATUS_META[k]?.label}
              <span className={`inline-flex items-center justify-center min-w-[18px] px-1 h-4 rounded-full text-[10px] font-bold ${active ? 'bg-white/20' : 'bg-zinc-100 text-zinc-500'}`}>
                {count}
              </span>
            </button>
          );
        })}
        {ddOptions.length > 0 && (
          <select value={ddFilter} onChange={(e) => setDdFilter(e.target.value)}
            className="h-8 rounded-full border border-teal-200 bg-white px-2.5 text-xs text-teal-700 outline-none"
            data-testid="pr-mine-dd-filter">
            <option value="all">納入先: すべて</option>
            {ddOptions.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
          </select>
        )}
        <div className="ml-auto text-xs text-zinc-400">{rows.length}件</div>
      </div>

      {fabs.length > 0 && (
        <div className="rounded-lg border border-sky-200 bg-sky-50/60 p-3" data-testid="pr-mine-fabs">
          <p className="mb-2 text-xs font-bold text-sky-900">
            製作依頼（技術 FAB）{fabs.length}件
          </p>
          <ul className="divide-y divide-sky-200">
            {fabs.map((f) => (
              <li key={f.id} className="flex flex-wrap items-center gap-2 py-1.5 text-sm"
                data-testid={`pr-mine-fab-${f.fab_no}`}>
                <span className="font-semibold text-sky-900">{f.fab_no}</span>
                <span className="text-zinc-800">{f.name}</span>
                <span className="text-xs text-zinc-600">
                  {f.qty}{f.uom || ''}{f.deliver_to_label ? ` · 納入先 ${f.deliver_to_label}` : ''}
                </span>
                <Badge variant="outline" className="ml-auto border-sky-300 text-sky-900">
                  {f.status_label}
                </Badge>
              </li>
            ))}
          </ul>
        </div>
      )}

      {loading && rows.length === 0 ? (
        <p className="py-16 text-center text-sm text-zinc-400">読み込み中...</p>
      ) : rows.length === 0 ? (
        <div className="py-16 text-center" data-testid="pr-mine-empty">
          <Article size={40} weight="duotone" className="text-zinc-300 mx-auto mb-2" />
          <p className="text-sm text-zinc-500">
            {fabs.length > 0 ? '部品依頼はまだありません（製作依頼は上に出ています）'
              : '依頼はまだありません'}
          </p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="pr-mine-list">
          {rows.map((r) => <MyRequestCard key={r.id} req={r} catMap={catMap} onCancel={() => cancel(r)} onEdit={() => onEdit?.(r)} />)}
        </div>
      )}
    </div>
  );
}

function MyRequestCard({ req, catMap, onCancel, onEdit }) {
  const status = STATUS_META[req.status] || STATUS_META.open;
  const priority = PRIORITY_META[req.priority] || PRIORITY_META.normal;
  const canCancel = req.status === 'open';
  const [expanded, setExpanded] = useState(false);
  const cat = req.order_category_id ? (catMap[req.order_category_id] || null) : null;
  const catName = cat?.name || req.order_category_name;

  return (
    <div className="bg-white rounded-xl border border-zinc-200 shadow-sm overflow-hidden" data-testid={`pr-mine-card-${req.id}`}>
      <button onClick={() => setExpanded(!expanded)} className="w-full text-left px-4 py-3 hover:bg-zinc-50 transition-colors flex items-start gap-3">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${status.badge.replace('border-', 'ring-1 ring-').replace('bg-', 'bg-')}`}>
          <status.Icon size={16} weight="duotone" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-mono text-zinc-500">{req.request_no}</span>
            <Badge variant="outline" className={status.badge}>{status.label}</Badge>
            <Badge variant="outline" className={priority.badge}>優先度: {priority.label}</Badge>
            {req.order_name && (
              <span className="px-1.5 py-0.5 rounded bg-orange-50 text-orange-700 border border-orange-200 text-[10px]" data-testid={`pr-mine-order-name-${req.id}`}>
                受注: {req.order_name}
              </span>
            )}
            {catName && (
              <span className="px-1.5 py-0.5 rounded bg-sky-50 text-sky-700 border border-sky-200 text-[10px]" data-testid={`pr-mine-order-cat-${req.id}`}>
                {catName}{cat?.symbol ? ` (${cat.symbol})` : ''}
              </span>
            )}
            {req.delivery_destination?.name && (
              <span className="px-1.5 py-0.5 rounded bg-teal-50 text-teal-700 border border-teal-200 text-[10px]"
                data-testid={`pr-mine-dd-${req.id}`}
                title={req.delivery_destination.formal_name || req.delivery_destination.name}>
                納入先: {req.delivery_destination.name}
              </span>
            )}
            {req.manufacturing_no && (
              <span className="px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 border border-indigo-200 text-[10px] font-mono" data-testid={`pr-mine-mno-${req.id}`}>
                作業指示: {req.manufacturing_no}
              </span>
            )}
            {req.techs_entered && (
              <span className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] font-semibold"
                data-testid={`pr-mine-techs-${req.id}`}
                title={`Techs入力済み（${req.techs_entered_by || ''} ${String(req.techs_entered_at || '').slice(0, 10)}）`}>
                Techs入力済{req.techs_order_no ? ` · 受注No ${req.techs_order_no}` : ''}
              </span>
            )}
            {(req.line_suppliers || []).length > 1 && (
              <span className="px-1.5 py-0.5 rounded bg-purple-50 text-purple-700 border border-purple-200 text-[10px] font-semibold"
                data-testid={`pr-mine-multisup-${req.id}`}
                title={`未発注の仕入先 ${(req.line_suppliers || []).length}社`}>
                {(req.line_suppliers || []).length}社
              </span>
            )}
            {(req.lines_total_count || 0) > 1 && (req.lines_ordered_count || 0) > 0 && (req.lines_ordered_count < req.lines_total_count) && (
              <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-300 text-[10px] font-semibold"
                data-testid={`pr-mine-partial-${req.id}`}
                title="一部の明細のみ発注済">
                {req.lines_ordered_count}/{req.lines_total_count} 発注済
              </span>
            )}
            <span className="text-xs text-zinc-400">{String(req.created_at || '').slice(0, 10)}</span>
          </div>
          <div className="mt-1 text-sm font-medium text-zinc-900 truncate">
            {req.product_name ? (
              <>
                {req.product_name} 用の部品
                <span className="ml-2 text-xs text-zinc-500">（{req.line_count || req.lines?.length || 0} 明細）</span>
              </>
            ) : (
              <>
                {req.first_line_name || req.lines?.[0]?.name || '(no items)'}
                {(req.line_count || req.lines?.length || 0) > 1 && (
                  <span className="ml-2 text-xs text-zinc-500">+{(req.line_count || req.lines?.length || 0) - 1}件</span>
                )}
              </>
            )}
          </div>
          <div className="text-xs text-zinc-500 mt-0.5">
            {req.total_qty_by_uom || (req.lines?.[0] ? `${req.lines[0].qty} ${req.lines[0].uom}` : '')}
            {req.needed_by && <span className="ml-3">希望納期: {req.needed_by}</span>}
          </div>
        </div>
        <ArrowRight size={14} weight="bold" className={`text-zinc-400 shrink-0 mt-2 transition-transform ${expanded ? 'rotate-90' : ''}`} />
      </button>

      {expanded && (
        <div className="border-t border-zinc-200 bg-zinc-50/50 px-4 py-3 space-y-3" data-testid={`pr-mine-detail-${req.id}`}>
          <TraceabilityPanel entityType="parts_request" entityId={req.id} />
          {(req.lines || []).map((ln, i) => (
            <div key={`${req.id}-ln-${i}`} className="bg-white rounded-lg border border-zinc-200 p-3 flex items-start gap-3">
              {ln.photo_url && (
                ln.photo_is_pdf ? (
                  <StorageLink
                    path={ln.photo_url}
                    target="_blank" rel="noreferrer"
                    className="w-12 h-12 rounded bg-rose-50 border border-rose-200 flex flex-col items-center justify-center shrink-0 hover:bg-rose-100"
                    title={ln.photo_filename || 'PDFを開く'}
                    data-testid={`pr-mine-attach-${i}`}
                  >
                    <FilePdf size={18} weight="duotone" className="text-rose-600" />
                    <span className="text-[8px] font-bold text-rose-700 mt-0.5">PDF</span>
                  </StorageLink>
                ) : (
                  <StorageLink
                    path={ln.photo_url}
                    target="_blank" rel="noreferrer"
                    title={ln.photo_filename || '画像を開く'}
                    data-testid={`pr-mine-attach-${i}`}
                  >
                    <StorageThumb path={ln.photo_url} alt="" className="w-12 h-12 rounded object-cover shrink-0" />
                  </StorageLink>
                )
              )}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-zinc-800">{ln.name || ln.part_no}</div>
                <div className="text-xs text-zinc-500 flex items-center gap-2 flex-wrap mt-0.5">
                  {ln.part_no && <span className="font-mono">{ln.part_no}</span>}
                  {ln.model_no && <span className="text-zinc-400">{ln.model_no}</span>}
                  {ln.maker && <span className="text-zinc-400">· {ln.maker}</span>}
                  <span className="tabular-nums">{ln.qty} {ln.uom}</span>
                </div>
                {ln.reference_url && (
                  <a href={ln.reference_url} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 text-xs text-sky-600 hover:text-sky-800 truncate max-w-full">
                    <LinkIcon size={11} weight="bold" /> {ln.reference_url}
                  </a>
                )}
                {ln.notes && <div className="text-xs text-zinc-500 mt-1">{ln.notes}</div>}
                {(ln.unit_records || []).length > 0 && (
                  <div className="mt-1 flex flex-wrap items-center gap-1" data-testid={`pr-mine-units-${i}`}>
                    <span className="text-[10px] text-zinc-400">
                      {ln.unit_records[0].label}に{ln.unit_records.length}件登録:
                    </span>
                    {ln.unit_records.map((u) => (
                      <span key={u.id} className="rounded bg-zinc-100 px-1.5 py-0.5 font-mono text-[10px] text-zinc-700">
                        {u.no}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
          {req.notes && <div className="text-xs text-zinc-500 px-1">全体備考: {req.notes}</div>}

          <div className="flex justify-end gap-2">
            {/* iter_143h · A4 receipt (発注依頼書) print — opens the standalone
                print page in a new tab. Available on every request card;
                stamps for 依頼者/承認者/資材担当承認 render on the receipt. */}
            <Button
              size="sm"
              variant="outline"
              className="h-8"
              onClick={() => window.open(`/shizai/requests/${req.id}/print`, '_blank', 'noopener,noreferrer')}
              data-testid={`pr-mine-print-${req.id}`}
              title="発注依頼書をA4で印刷"
            >
              <Printer size={12} className="mr-1" /> 発注依頼書を印刷
            </Button>
            {canCancel && (
              <Button size="sm" variant="outline" className="h-8 text-sky-600 hover:bg-sky-50" onClick={onEdit} data-testid={`pr-mine-edit-${req.id}`}>
                <PencilSimple size={12} className="mr-1" /> 編集
              </Button>
            )}
            {canCancel && (
              <Button size="sm" variant="outline" className="h-8 text-rose-600 hover:bg-rose-50" onClick={onCancel} data-testid={`pr-mine-cancel-${req.id}`}>
                <TrashSimple size={12} className="mr-1" /> 取消
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
